import * as React from 'react';
import {
  first, isEmpty, map,
} from 'lodash';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { Notice } from '@affiliates/AspireUI';
import {
  OfferDetails,
  ShopifyScopesMissing,
} from '@affiliates/components';
import { EmailComposeButton } from '@affiliates/containers';
import {
  ISTAComposerMemberFieldIds,
  MEMBER_FIRST_NAME,
  OfferDetailsQueriesStatus,
  STA_FIELD_NAME,
} from '@affiliates/hooks';
import { OFFER_SOURCE, OFFER_STATUS, UserInfoInput } from '@affiliates/types/globalTypes';
import { IDateRangeSettings } from '@frontend/app/components';
import { Modal, Typography } from '@revfluence/fresh';
import { useOfferDetailsContext } from '@frontend/context/OfferDetailsContext';
import moment from 'moment';
import { CircleExclamationIcon } from '@revfluence/fresh-icons/regular';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { OfferLogDrawer } from '../components/OfferLogDrawer';
import ShopifyStoreDisconnectionAlert from '../components/ShopifyStoreDisconnectionAlert/ShopifyStoreDisconnectionAlert';
import { TOfferMember } from '../components/MemberTable/types';
import OfferDashboardTabs from '../components/OfferDashboardTabs/OfferDashboardTabs';
import { RefreshMemberTable } from '../components/MemberTable/RefreshMemberTable';
import { RefreshManageOffer } from '../components/ManageOffer/RefreshManageOffer';
import { RefreshMemberConversionDrawer } from '../components/RefreshMemberConversionDrawer/RefreshMemberConversionDrawer';
import { RefreshMembersWizard } from '../containers/MembersWizard/RefreshMembersWizard';
import { BoostApprovalDrawer } from '../components/BoostApprovalDrawer';
import { useGetBoostPerformanceData } from '../hooks/useGetBoostPerformanceData';
import PFAApprovalDrawer from '../components/PFAApprovalDrawer';
import { useGetPFAOrderIds } from '../hooks/useGetPFAOrderIds';
import { LoadingState } from '../components/OfferDetails/LoadingState';
import { StatsCards } from '../components/OfferDetails/StatsCards';
import { FiltersAndAlerts } from '../components/OfferDetails/FiltersAndAlerts';
import { useRefreshOfferDetailsQueries } from '../hooks/useRefreshOfferDetailsQueries';

const { useState, useEffect } = React;
interface IProps {
  baseUri: string;
  dateRangeSettings: IDateRangeSettings;
  shopifyAppId: string;
  staComposerMemberFieldIds: ISTAComposerMemberFieldIds;
  profile: UserInfoInput;
  brandName?: string;
  onDeletedMembersChange?: (members: readonly TOfferMember[]) => void;
  selectedPayoutIds: number[];
  setSelectedPayoutIds: (ids: number[]) => void;
  selectedLastPayoutIds?: number[];
  setIsMemberTableFilterOpen: (open: boolean) => void;
  numericFilters: Record<string, string[]>;
  setNumericFilters: (filters: Record<string, string[]>) => void;
  selectedStatusFilters: string[];
  setSelectedStatusFilters: (filters: string[]) => void;
  selectedBoostTypes: string[];
  setSelectedBoostTypes: (types: string[]) => void;
  selectedBoostStatuses: string[];
  setSelectedBoostStatuses: (statuses: string[]) => void;
  setSelectedLastPayoutIds: (ids: number[]) => void;
}
interface IParams {
  offerId: string;
}
interface StateType {
  isUpdated: boolean;
}

function formatArchivedMessage(archivedDate: string | null, archivedUserName: string | null): string {
  if (!archivedDate || !archivedUserName) return '';

  return `This offer was archived on ${moment(archivedDate).format('MM/DD/YY')} by ${archivedUserName}. All offer insights are still available but you can no longer add new members.`;
}

export const RefreshOfferDetailsPage: React.FC<Readonly<IProps>> = (props) => {
  const {
    baseUri, dateRangeSettings, shopifyAppId, staComposerMemberFieldIds, profile, brandName, onDeletedMembersChange,
    selectedPayoutIds,
    setSelectedPayoutIds,
    selectedLastPayoutIds,
    setIsMemberTableFilterOpen,
    numericFilters,
    setNumericFilters,
    selectedStatusFilters,
    setSelectedStatusFilters,
    selectedBoostTypes,
    setSelectedBoostTypes,
    selectedBoostStatuses,
    setSelectedBoostStatuses,
    setSelectedLastPayoutIds,
  } = props;

  const { Text } = Typography;
  const location = useLocation<StateType | undefined>();
  const {
    migrateToGraphQL,
    enableMultipleShopify: isEnabledMultipleShopify,
    archiveOffer,
    refreshUi,
  } = useClientFeatures();
  const { offerId: offerIdStr } = useParams<IParams>();
  const offerId = Number(offerIdStr);
  const { isManageDeepLinksAllowed, linkForPromo, shopifyLandingPage } = useClientFeatures();
  const {
    setOffer,
    isOfferLogDrawerVisible,
    setIsOfferLogDrawerVisible,
    setShowMigrationModal,
    isOldOffer,
    setOfferFormValue,
  } = useOfferDetailsContext();

  // Use the updated queries hook
  const queries = useRefreshOfferDetailsQueries(offerId, dateRangeSettings, isEnabledMultipleShopify, onDeletedMembersChange);
  // commenting for now will add once we fix the stats
  // const { data: tierPerformanceData } = useGetTierPerformanceData({
  //   fetchPolicy: 'network-only',
  //   variables: {
  //     offerId,
  //   },
  // });
  const { data: boostsPerformanceData, refetch: refetchBoostsPerformanceData, loading: isBoostsPerformanceDataLoading } = useGetBoostPerformanceData({
    fetchPolicy: 'network-only',
    variables: {
      offerId,
    },
  });

  const history = useHistory();
  const [isNotifyModalOpen, openNotifyMemberModal] = useState(false);
  const [isBulkBoostsModalOpen, setIsBulkBoostsModalOpen] = useState(false);
  const [isPFAApprovalDrawerOpen, setIsPFAApprovalDrawerOpen] = useState(false);
  const state = location.state;
  const closeNotifyModal = () => {
    if (state) {
      state.isUpdated = false;
    }
    openNotifyMemberModal(false);
  };

  const hasEffectRun = React.useRef(false);
  useEffect(() => {
    setOffer(null);
    setOfferFormValue(null);
    setIsOfferLogDrawerVisible(false);
  }, [setIsOfferLogDrawerVisible, setOffer, setOfferFormValue]);
  useEffect(() => {
    if (queries.status === OfferDetailsQueriesStatus.Ready) {
      setOffer(queries.offerDetails);
      dateRangeSettings.setEarliestDate(queries.offerDetails.createdDate);
    }
    if (!hasEffectRun.current && queries.status === OfferDetailsQueriesStatus.Ready) {
      const affiliateOfferLinkGenerated = first(queries.offerDetails.links)?.affiliateOfferLinkGenerated;
      hasEffectRun.current = true;
      if (affiliateOfferLinkGenerated && state && state?.isUpdated) {
        openNotifyMemberModal(true);
      }
    }
  }, [queries, dateRangeSettings, openNotifyMemberModal, state, setOffer]);

  useEffect(() => {
    if (
      queries.status === OfferDetailsQueriesStatus.Ready
      && queries.offerDetails?.archivedDate !== null
      && archiveOffer
    ) {
      Modal.warning({
        title: 'Archive Offer',
        content: formatArchivedMessage(
          queries.offerDetails?.archivedDate,
          queries.offerDetails?.archivedUser?.name,
        ),
        icon: <CircleExclamationIcon />,
        autoFocusButton: null,
        okText: 'Continue',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries.status, archiveOffer]);

  // Call hook unconditionally at the top level
  const pfaOrderIdsResult = useGetPFAOrderIds(
    queries.status === OfferDetailsQueriesStatus.Ready ? queries.boostData : null,
    queries.status === OfferDetailsQueriesStatus.Ready ? queries.updateMembers?.selectedMembers : null
  );

  // Handle the result conditionally
  const pfaOrderIds = queries.status === OfferDetailsQueriesStatus.Ready
    ? pfaOrderIdsResult?.pfaOrderIds || []
    : [];

  // get all pfa order ids for all members in the offer
  const allPFAOrderIdsResult = useGetPFAOrderIds(
    queries.status === OfferDetailsQueriesStatus.Ready ? queries.boostData : null,
    queries.status === OfferDetailsQueriesStatus.Ready ? queries.offerMembers as TOfferMember[] : null,
  );

  // get all pfa order ids for all members in the offer
  const allPfaOrderIds = queries.status === OfferDetailsQueriesStatus.Ready
    ? allPFAOrderIdsResult?.pfaOrderIds || []
    : [];

  const getOfferDetailsPageContents = () => {
    switch (queries.status) {
      case OfferDetailsQueriesStatus.Loading:
        return <LoadingState />;
      case OfferDetailsQueriesStatus.Failed:
        return <Notice type="error" message={queries.error.message} />;
      case OfferDetailsQueriesStatus.Ready:
        let offerStatus = null;

        let source = OFFER_SOURCE.SHOPIFY;
        let isMultipleShopifyOffer: boolean = false;
        if (!isEmpty(queries.offerDetails.promos)) {
          source = OFFER_SOURCE.SHOPIFY;
          isMultipleShopifyOffer = first(queries.offerDetails.promos)?.multiShopifyEnabled;
          if (queries.missingShopifyCredentials) {
            offerStatus = OFFER_STATUS.PAUSED;
          } else {
            offerStatus = first(queries.offerDetails.promos).status;
          }
        } else if (!isEmpty(queries.offerDetails.links) && !queries.offerDetails.isPromoLink) {
          source = OFFER_SOURCE.TUNE;
          offerStatus = first(queries.offerDetails.links).status;
        }
        let includedMemberIds = queries.memberTable?.selectedAffiliateIds || [];
        if (isEmpty(includedMemberIds)) {
          includedMemberIds = map(queries.offerMembers || [], (m) => m.memberId);
        }
        const disableCompose = queries.memberTable?.disableCompose
          || staComposerMemberFieldIds[MEMBER_FIRST_NAME] < 1
          || staComposerMemberFieldIds[STA_FIELD_NAME] < 1;
        const emailComposeButton = (
          <EmailComposeButton
            disableCompose={disableCompose}
            includeMemberIds={includedMemberIds}
            offer={queries.offerDetails}
            staComposerMemberFieldIds={staComposerMemberFieldIds}
            source={source}
            refreshSTADashboard
          />
        );

        const emailUpdateComposeButton = (
          <EmailComposeButton
            disableCompose={disableCompose}
            includeMemberIds={includedMemberIds}
            offer={queries.offerDetails}
            staComposerMemberFieldIds={staComposerMemberFieldIds}
            source={source}
            profile={profile}
            isSourceEditOffer
            brandName={brandName}
          />
        );

        const includeFailedMembersWizard = !isEmpty(queries.failedMembers?.members)
          && !(source === OFFER_SOURCE.SHOPIFY && queries.missingShopifyCredentials);
        const data = queries.offerStats;
        return (
          <>
            {/* Filters and Alerts Section */}
            <FiltersAndAlerts
              dateRangeSettings={dateRangeSettings}
              selectedPayoutIds={selectedPayoutIds}
              setSelectedPayoutIds={setSelectedPayoutIds}
              selectedLastPayoutIds={selectedLastPayoutIds}
              numericFilters={numericFilters}
              setNumericFilters={setNumericFilters}
              selectedStatusFilters={selectedStatusFilters}
              setSelectedStatusFilters={setSelectedStatusFilters}
              selectedBoostTypes={selectedBoostTypes}
              setSelectedBoostTypes={setSelectedBoostTypes}
              selectedBoostStatuses={selectedBoostStatuses}
              setSelectedBoostStatuses={setSelectedBoostStatuses}
              setIsMemberTableFilterOpen={setIsMemberTableFilterOpen}
              offerDetails={queries.offerDetails}
              shopifyLandingPage={shopifyLandingPage}
              linkForPromo={linkForPromo}
              isLandingPageEnabled={queries.offerDetails.isLandingPageEnabled}
              offerId={offerId}
              baseUri={baseUri}
              isOfferExpired={queries.offerDetails.expired || queries.offerDetails?.archivedDate}
              archivedDate={queries.offerDetails.archivedDate}
              archivedUserName={queries.offerDetails?.archivedUser?.name}
              landingPageUrl={queries.offerDetails.landingPageUrl}
              migrateToGraphQL={migrateToGraphQL}
              isOldOffer={isOldOffer}
              setShowMigrationModal={setShowMigrationModal}
              boostData={queries.boostData}
              setSelectedLastPayoutIds={setSelectedLastPayoutIds}
            />
            <div className="p-4 space-y-4">
              <StatsCards
                data={data}
                queries={queries}
                isMembersLoading={false}
                memberConversionCount={data?.memberConversionCount}
                membersWithPayoutDue={data?.membersWithPayoutDue}
                isBoostsPerformanceDataLoading={isBoostsPerformanceDataLoading}
                boostsPerformanceData={boostsPerformanceData?.boostPerformances}
              />
              {source === OFFER_SOURCE.SHOPIFY && (
                queries.missingShopifyCredentials ? (
                  <ShopifyScopesMissing
                    onClick={() => {
                      history.push({
                        ...location,
                        pathname: `/settings/${encodeURIComponent(shopifyAppId)}`,
                      });
                    }}
                  />
                ) : (
                  <ShopifyStoreDisconnectionAlert refreshUi={refreshUi} />

                )
              )}
              <OfferDashboardTabs storeWiseConversions={queries.multipleShopifyOfferStats} tierPerformanceData={null} boostsPerformanceData={{ data: boostsPerformanceData?.boostPerformances, boosts: queries.boostData }} />
              <RefreshMemberTable
                buttonActions={queries.memberTable?.buttonActions}
                emailComposeButton={emailComposeButton}
                fixFailedMembers={queries.failedMembers?.showFailedMembers}
                members={queries.offerMembers || []}
                missingShopifyCredentials={source === OFFER_SOURCE.SHOPIFY && queries.missingShopifyCredentials}
                offer={queries.offerDetails}
                currencies={queries.offerStats?.currencies}
                offerSource={source}
                offerStatus={offerStatus}
                onSelectMembers={queries.memberTable?.onSelectMembers}
                selectedAffiliateIds={queries.memberTable?.selectedAffiliateIds || []}
                failedPromoCodeErrors={queries.failedPromoCodeErrors}
                showStoreLevelAffiliateStats={queries.multipleShopifyAffiliateStats?.visible}
                connectedStores={queries.multipleShopifyOfferStats?.map((s) => ({
                  storeName: s.storeName,
                  clientShopifyConnectionId: s.clientShopifyConnectionId,
                })) || []}
                affiliatesStats={queries.multipleShopifyAffiliateStats?.affiliatesStats}
                shopifyStoreName={queries.shopifyStoreName}
                onComplete={queries.refresh}
                isMultipleShopifyOffer={isMultipleShopifyOffer}
                selectedPayoutIds={selectedPayoutIds}
                selectedLastPayoutIds={selectedLastPayoutIds}
                setIsBulkBoostsModalOpen={setIsBulkBoostsModalOpen}
                setIsPFAApprovalDrawerOpen={setIsPFAApprovalDrawerOpen}
                numericFilters={numericFilters}
                selectedStatusFilters={selectedStatusFilters}
                selectedBoostTypes={selectedBoostTypes}
                selectedBoostStatuses={selectedBoostStatuses}
                isFilterApplied={selectedPayoutIds.length > 0 || Object.keys(numericFilters).length > 0 || selectedStatusFilters.length > 0 || selectedLastPayoutIds?.length > 0 || queries.isLoadingMembers || selectedBoostTypes.length > 0 || selectedBoostStatuses.length > 0}
                affiliatesData={queries.affiliatesData}
                boostData={queries.boostData}
                isLoadingMembers={queries.isLoadingMembers}
                pfaOrderIds={allPfaOrderIds}
              />
              {queries.status === OfferDetailsQueriesStatus.Ready && (
                <>
                  <RefreshMembersWizard
                    offerSource={source}
                    mode={queries.updateMembers?.mode}
                    offer={queries.offerDetails}
                    onClose={queries.updateMembers?.onClose}
                    onComplete={queries.refresh}
                    selectedMembers={queries.updateMembers?.selectedMembers}
                    refreshDatesOnly={queries.updateMembers?.refreshDatesOnly}
                    visible={queries.updateMembers?.visible}
                    failedPromoCodeErrors={queries.failedPromoCodeErrors}
                  />
                  {includeFailedMembersWizard && (
                    <RefreshMembersWizard
                      offerSource={source}
                      mode="refresh"
                      offer={queries.offerDetails}
                      onClose={queries.failedMembers?.dismissFailedMembers}
                      onComplete={queries.refresh}
                      refreshDatesOnly={false}
                      selectedMembers={queries.failedMembers?.members}
                      visible={queries.failedMembers?.visible}
                      failedPromoCodeErrors={queries.failedPromoCodeErrors}
                    />
                  )}
                  <RefreshManageOffer
                    onComplete={queries.refresh}
                    offer={queries.offerDetails}
                    affiliatesData={queries.affiliatesData}
                    visible={queries.updateManageOffer?.visible}
                    offerSource={source}
                    selectedMembers={queries.updateMembers?.selectedMembers}
                    onClose={queries.updateManageOffer?.onClose}
                    manageDeepLinksAllowed={isManageDeepLinksAllowed}
                  />
                  <RefreshMemberConversionDrawer
                    offer={queries.offerDetails}
                    source={source}
                    isOpen={queries.memberConversion?.visible}
                    selectedMemberConversion={queries.memberConversion?.selectedMemberConversion}
                    onClose={queries.memberConversion?.onClose}
                    refresh={queries.refresh}
                    dateRangeSettings={dateRangeSettings}
                    boostData={queries.boostData}
                    affiliatesData={queries.affiliatesData}
                    refetchBoostsPerformanceData={refetchBoostsPerformanceData}

                  />
                </>
              )}

              {/* <MigrationModelContent
                baseUri={baseUri}
                showMigrationModal={showMigrationModal}
                setShowMigrationModal={setShowMigrationModal}
                offer={queries.offerDetails}
                source={source}
                profile={profile}
                onComplete={queries.refresh}
              /> */}

              <OfferLogDrawer
                offerId={queries.offerDetails.id}
                onClose={() => setIsOfferLogDrawerVisible(false)}
                open={isOfferLogDrawerVisible}
                source={source}
              />
              {!isEmpty(includedMemberIds) && (
                <Modal
                  title="Notify members"
                  open={isNotifyModalOpen}
                  onOk={closeNotifyModal}
                  onCancel={closeNotifyModal}
                  cancelText="Don't Notify"
                  okText={emailUpdateComposeButton}
                  okType="primary"
                >
                  <Text>Do you want to send an email to notify all the members in this offer of the changes?</Text>
                  <br />
                  <Text>Changes will be directly reflected in the creator portal.</Text>
                </Modal>
              )}
              {isBulkBoostsModalOpen && <BoostApprovalDrawer
                open={isBulkBoostsModalOpen}
                setIsBulkBoostsModalOpen={setIsBulkBoostsModalOpen}
                selectedMembers={queries.updateMembers?.selectedMembers}
                offerBoosts={queries.boostData}
                refresh={queries.refresh}
                refetchBoostsPerformanceData={refetchBoostsPerformanceData}
              />}
              <PFAApprovalDrawer
                open={isPFAApprovalDrawerOpen}
                setIsPFAApprovalDrawerOpen={setIsPFAApprovalDrawerOpen}
                selectedMembers={queries.updateMembers.selectedMembers}
                offerId={offerId}
                refresh={queries.refresh}
                pfaOrderIds={pfaOrderIds}
              />
            </div>
          </>
        );
    }
  };
  return <OfferDetails>{getOfferDetailsPageContents()}</OfferDetails>;
};
